import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Package,
  MessageSquare,
  FlaskConical,
  Loader2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { getSharedView, type SharedViewData, type SharedViewResult } from '../api/shares';

type LoadState =
  | { status: 'loading' }
  | SharedViewResult;

const KIND_LABELS: Record<string, string> = {
  skill: '技能（Skill）',
  mcp: 'MCP 工具',
  kb: '知识库（KB）',
  preset: '能力预设',
};

const formatTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

export default function ShareView() {
  const { token } = useParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // 公开页面：领域模块内部用原生 fetch，不携带任何认证头，
    // 避免 axios 拦截器把未登录访客重定向到登录页
    const tick = async () => {
      const next = await getSharedView(token);
      if (!cancelled) setState(next);
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="container" style={{ maxWidth: 860, margin: '0 auto', padding: '32px 16px' }}>
      <div className="flex items-center gap-2 mb-4">
        <strong style={{ fontSize: 18 }}>Atelier 分享</strong>
        <span className="badge secondary">只读视图</span>
        {state.status === 'ok' && state.data.expires_at && (
          <span className="text-muted text-sm flex items-center gap-1" style={{ marginLeft: 'auto' }}>
            <Clock size={13} /> {formatTime(state.data.expires_at)} 过期
          </span>
        )}
      </div>

      {state.status === 'loading' && (
        <div className="card text-center" style={{ padding: 48 }}>
          <Loader2 size={22} className="animate-spin inline" />
          <p className="text-muted mt-2">加载分享内容...</p>
        </div>
      )}

      {state.status === 'expired' && (
        <div className="card text-center" style={{ padding: 48 }}>
          <AlertTriangle size={22} className="inline text-error" />
          <h3 className="mt-2">分享链接已过期</h3>
          <p className="text-muted">该链接已超过有效期，请向分享者索取新的链接。</p>
        </div>
      )}

      {state.status === 'notfound' && (
        <div className="card text-center" style={{ padding: 48 }}>
          <AlertTriangle size={22} className="inline text-error" />
          <h3 className="mt-2">分享不存在</h3>
          <p className="text-muted">链接无效，或分享的内容已被删除。</p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="card text-center" style={{ padding: 48 }}>
          <AlertTriangle size={22} className="inline text-error" />
          <h3 className="mt-2">加载失败</h3>
          <p className="text-muted">{state.message}</p>
        </div>
      )}

      {state.status === 'ok' && <ShareContent data={state.data} />}
    </div>
  );
}

function ShareContent({ data }: { data: SharedViewData }) {
  if (data.resource_type === 'capability_pack' && data.capability_pack) {
    const pack = data.capability_pack;
    return (
      <div className="card">
        <h3>
          <Package size={18} className="inline mr-1" /> 能力包
        </h3>
        <div className="flex items-center gap-2 mt-2">
          <strong style={{ fontSize: 16 }}>{pack.name}</strong>
          <span className="badge secondary">{KIND_LABELS[pack.kind] || pack.kind}</span>
          <span className="badge secondary">v{pack.version}</span>
          {pack.framework && <span className="badge secondary">{pack.framework}</span>}
        </div>
        {pack.manifest && Object.keys(pack.manifest).length > 0 && (
          <div className="mt-4">
            <div className="text-sm text-muted mb-1">清单（已脱敏）</div>
            <pre
              style={{
                background: 'var(--bg-tertiary)',
                padding: 12,
                borderRadius: 'var(--radius-md)',
                overflow: 'auto',
                fontSize: 12,
              }}
            >
              {JSON.stringify(pack.manifest, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  }

  if (data.resource_type === 'session' && data.session) {
    const session = data.session;
    return (
      <div className="card">
        <h3>
          <MessageSquare size={18} className="inline mr-1" /> 会话记录
        </h3>
        <p className="text-muted text-sm mt-1">
          类型：{session.session_type} · 状态：{session.status} · 开始于 {formatTime(session.created_at)}
        </p>
        <div className="chat-messages mt-4">
          {session.messages.length === 0 && <p className="text-muted">（暂无消息）</p>}
          {session.messages.map((msg, i) => (
            <div key={i} className={`message ${msg.role === 'user' ? 'user' : 'agent'}`}>
              <div>{msg.content}</div>
              {msg.created_at && (
                <small style={{ opacity: 0.6, display: 'block', marginTop: 4 }}>{formatTime(msg.created_at)}</small>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.resource_type === 'eval_report' && data.eval_report) {
    const report = data.eval_report;
    return (
      <div className="card">
        <h3>
          <FlaskConical size={18} className="inline mr-1" /> 评测报告
        </h3>
        <div className="flex items-center gap-2 mt-2">
          <strong style={{ fontSize: 16 }}>{report.artifact.name}</strong>
          <span className="badge secondary">{KIND_LABELS[report.artifact.kind] || report.artifact.kind}</span>
          <span className="badge secondary">v{report.artifact.version}</span>
        </div>
        {report.runs.length === 0 ? (
          <p className="text-muted mt-4">（暂无评测运行记录）</p>
        ) : (
          <div className="list mt-4">
            {report.runs.map((run, i) => (
              <div key={i} className="list-item">
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <strong>{run.suite_id || '评测套件'}</strong>
                    {run.version && <span className="badge secondary text-xs">v{run.version}</span>}
                  </div>
                  <div className="text-muted text-sm mt-1">
                    通过率 {run.passed ?? 0}/{run.total ?? 0}
                    {typeof run.pass_rate === 'number' && `（${Math.round(run.pass_rate * 100)}%）`}
                    {typeof run.composite_score === 'number' && ` · 综合分 ${run.composite_score.toFixed(2)}`}
                    {run.run_at && ` · ${formatTime(run.run_at)}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return <p className="text-muted">未知的分享类型。</p>;
}
